/**
 * Client-side bitmap-to-SVG tracing using Canvas API.
 * Supports contour tracing (edge detection) and color region segmentation.
 */

export type TracingMode = "contour" | "color_region";

export interface TracingOptions {
  mode: TracingMode;
  /** Number of colors for color region mode (2-16). Default 6. */
  colorCount?: number;
  /** Edge threshold for contour mode (0-255). Default 40. */
  edgeThreshold?: number;
  /** Path simplification tolerance (0-10). Default 2. */
  simplifyTolerance?: number;
  /** Minimum region size in pixels for color mode. Default 100. */
  minRegionSize?: number;
  /** Output width. Default: source width. */
  outputWidth?: number;
  /** Output height. Default: source height. */
  outputHeight?: number;
  /** Stroke width for contour mode. Default 1.5. */
  strokeWidth?: number;
  /** Stroke color for contour mode. */
  strokeColor?: string;
  /** Background color. null = transparent. */
  backgroundColor?: string | null;
}

const DEFAULT_OPTIONS: Required<TracingOptions> = {
  mode: "contour",
  colorCount: 6,
  edgeThreshold: 40,
  simplifyTolerance: 2,
  minRegionSize: 100,
  outputWidth: 0,
  outputHeight: 0,
  strokeWidth: 1.5,
  strokeColor: "#1a1a2e",
  backgroundColor: null,
};

export interface TracingResult {
  svg: string;
  width: number;
  height: number;
  mode: TracingMode;
  colorCount?: number;
  /** Data URL for quick preview. */
  dataUrl: string;
}

/** Load an image URL into a Canvas and return its pixel data. */
async function loadImageData(
  src: string,
  maxDim = 1024,
): Promise<{ data: ImageData; canvas: HTMLCanvasElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      // Scale down for performance
      if (w > maxDim || h > maxDim) {
        const scale = maxDim / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, w, h);
      resolve({ data: ctx.getImageData(0, 0, w, h), canvas, width: w, height: h });
    };
    img.onerror = () => reject(new Error("Failed to load image for tracing"));
    img.src = src;
  });
}

// ── Contour Tracing ─────────────────────────────────────────────────

function sobelEdgeDetection(imageData: ImageData, threshold: number): Uint8Array {
  const { width, height, data } = imageData;
  const gray = new Uint8Array(width * height);
  // Convert to grayscale
  for (let i = 0; i < gray.length; i++) {
    const idx = i * 4;
    gray[i] = Math.round(data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114);
  }
  const edges = new Uint8Array(width * height);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      // Sobel kernels
      const gx =
        -gray[(y - 1) * width + (x - 1)] + gray[(y - 1) * width + (x + 1)] +
        -2 * gray[y * width + (x - 1)] + 2 * gray[y * width + (x + 1)] +
        -gray[(y + 1) * width + (x - 1)] + gray[(y + 1) * width + (x + 1)];
      const gy =
        -gray[(y - 1) * width + (x - 1)] - 2 * gray[(y - 1) * width + x] - gray[(y - 1) * width + (x + 1)] +
        gray[(y + 1) * width + (x - 1)] + 2 * gray[(y + 1) * width + x] + gray[(y + 1) * width + (x + 1)];
      const mag = Math.sqrt(gx * gx + gy * gy);
      edges[idx] = mag > threshold ? 1 : 0;
    }
  }
  return edges;
}

/** Trace contour paths from edge pixels using a simple chain-code follower. */
function traceContourPaths(edges: Uint8Array, width: number, height: number, simplify: number): string[][] {
  const visited = new Uint8Array(edges.length);
  const paths: string[][] = [];

  // Direction offsets: right, down-right, down, down-left, left, up-left, up, up-right
  const dx = [1, 1, 0, -1, -1, -1, 0, 1];
  const dy = [0, 1, 1, 1, 0, -1, -1, -1];

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      if (edges[idx] === 0 || visited[idx]) continue;

      // Follow the contour
      const points: [number, number][] = [[x, y]];
      visited[idx] = 1;
      let cx = x, cy = y;
      let found = true;

      while (found && points.length < 10000) {
        found = false;
        for (let d = 0; d < 8; d++) {
          const nx = cx + dx[d];
          const ny = cy + dy[d];
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
          const ni = ny * width + nx;
          if (edges[ni] === 1 && !visited[ni]) {
            visited[ni] = 1;
            cx = nx;
            cy = ny;
            points.push([cx, cy]);
            found = true;
            break;
          }
        }
      }

      if (points.length < 5) continue; // Skip tiny fragments

      // Simplify path (Ramer-Douglas-Peucker)
      const simplified = rdpSimplify(points, simplify);
      if (simplified.length < 3) continue;

      // Convert to SVG path commands
      const pathCmds: string[] = [`M${simplified[0][0]},${simplified[0][1]}`];
      for (let i = 1; i < simplified.length; i++) {
        pathCmds.push(`L${simplified[i][0]},${simplified[i][1]}`);
      }
      paths.push(pathCmds);
    }
  }
  return paths;
}

function rdpSimplify(points: [number, number][], tolerance: number): [number, number][] {
  if (points.length <= 2) return points;
  let maxDist = 0;
  let maxIdx = 0;
  const [sx, sy] = points[0];
  const [ex, ey] = points[points.length - 1];
  const dx = ex - sx, dy = ey - sy;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;

  for (let i = 1; i < points.length - 1; i++) {
    const dist = Math.abs(dy * points[i][0] - dx * points[i][1] + ex * sy - ey * sx) / len;
    if (dist > maxDist) { maxDist = dist; maxIdx = i; }
  }

  if (maxDist > tolerance) {
    const left = rdpSimplify(points.slice(0, maxIdx + 1), tolerance);
    const right = rdpSimplify(points.slice(maxIdx), tolerance);
    return [...left.slice(0, -1), ...right];
  }
  return [points[0], points[points.length - 1]];
}

function buildContourSvg(
  paths: string[][],
  width: number, height: number,
  opts: Required<TracingOptions>,
): string {
  const bgRect = opts.backgroundColor
    ? `<rect width="${width}" height="${height}" fill="${opts.backgroundColor}"/>`
    : "";
  const pathEls = paths.map(
    (cmds) => `<path d="${cmds.join(" ")}" fill="none" stroke="${opts.strokeColor}" stroke-width="${opts.strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>`
  ).join("\n  ");

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
  ${bgRect}
  ${pathEls}
</svg>`;
}

// ── Color Region Tracing ────────────────────────────────────────────

interface ColorRegion {
  color: [number, number, number];
  pixels: Set<number>;
}

function quantizeColors(imageData: ImageData, numColors: number): { palette: [number, number, number][]; indexed: Uint8Array } {
  const { data, width, height } = imageData;
  const pixelCount = width * height;

  // Simple median-cut approximation: sample + k-means
  const samples: [number, number, number][] = [];
  const step = Math.max(1, Math.floor(pixelCount / 2000));
  for (let i = 0; i < pixelCount; i += step) {
    const idx = i * 4;
    samples.push([data[idx], data[idx + 1], data[idx + 2]]);
  }

  // Initialize centroids by evenly spacing through samples
  const centroids: [number, number, number][] = [];
  const cStep = Math.max(1, Math.floor(samples.length / numColors));
  for (let i = 0; i < numColors && i * cStep < samples.length; i++) {
    centroids.push([...samples[i * cStep]]);
  }
  while (centroids.length < numColors) {
    centroids.push([...samples[Math.floor(Math.random() * samples.length)]]);
  }

  // K-means iterations
  for (let iter = 0; iter < 10; iter++) {
    const sums = centroids.map(() => [0, 0, 0, 0] as [number, number, number, number]);
    for (const s of samples) {
      let bestD = Infinity, bestC = 0;
      for (let c = 0; c < centroids.length; c++) {
        const d = (s[0] - centroids[c][0]) ** 2 + (s[1] - centroids[c][1]) ** 2 + (s[2] - centroids[c][2]) ** 2;
        if (d < bestD) { bestD = d; bestC = c; }
      }
      sums[bestC][0] += s[0]; sums[bestC][1] += s[1]; sums[bestC][2] += s[2]; sums[bestC][3]++;
    }
    for (let c = 0; c < centroids.length; c++) {
      if (sums[c][3] > 0) {
        centroids[c] = [
          Math.round(sums[c][0] / sums[c][3]),
          Math.round(sums[c][1] / sums[c][3]),
          Math.round(sums[c][2] / sums[c][3]),
        ];
      }
    }
  }

  // Assign each pixel to nearest centroid
  const indexed = new Uint8Array(pixelCount);
  for (let i = 0; i < pixelCount; i++) {
    const idx = i * 4;
    let bestD = Infinity, bestC = 0;
    for (let c = 0; c < centroids.length; c++) {
      const d = (data[idx] - centroids[c][0]) ** 2 + (data[idx + 1] - centroids[c][1]) ** 2 + (data[idx + 2] - centroids[c][2]) ** 2;
      if (d < bestD) { bestD = d; bestC = c; }
    }
    indexed[i] = bestC;
  }

  return { palette: centroids, indexed };
}

/** Trace region boundaries using marching squares for each color. */
function traceColorRegions(
  indexed: Uint8Array,
  palette: [number, number, number][],
  width: number, height: number,
  minRegionSize: number,
  simplify: number,
): { color: string; paths: string[] }[] {
  const results: { color: string; paths: string[] }[] = [];

  for (let c = 0; c < palette.length; c++) {
    // Create binary mask for this color
    const mask = new Uint8Array(width * height);
    let count = 0;
    for (let i = 0; i < indexed.length; i++) {
      if (indexed[i] === c) { mask[i] = 1; count++; }
    }
    if (count < minRegionSize) continue;

    // Trace boundary using marching squares
    const contours = marchingSquares(mask, width, height, simplify);
    if (contours.length === 0) continue;

    const [r, g, b] = palette[c];
    const hexColor = `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
    const svgPaths = contours.map((pts) => {
      const cmds = [`M${pts[0][0]},${pts[0][1]}`];
      for (let i = 1; i < pts.length; i++) {
        cmds.push(`L${pts[i][0]},${pts[i][1]}`);
      }
      cmds.push("Z");
      return cmds.join(" ");
    });

    results.push({ color: hexColor, paths: svgPaths });
  }

  return results;
}

function marchingSquares(
  mask: Uint8Array, width: number, height: number, simplify: number,
): [number, number][][] {
  const contours: [number, number][][] = [];
  const visited = new Set<string>();

  for (let y = 0; y < height - 1; y++) {
    for (let x = 0; x < width - 1; x++) {
      // Check if this is a boundary cell
      const tl = mask[y * width + x];
      const tr = mask[y * width + x + 1];
      const bl = mask[(y + 1) * width + x];
      const br = mask[(y + 1) * width + x + 1];
      const cell = (tl << 3) | (tr << 2) | (br << 1) | bl;

      if (cell === 0 || cell === 15) continue; // Fully inside or outside
      const key = `${x},${y}`;
      if (visited.has(key)) continue;

      // Follow the contour from this cell
      const points: [number, number][] = [];
      let cx = x, cy = y;
      let prevDir = -1;
      let steps = 0;

      do {
        const k = `${cx},${cy}`;
        visited.add(k);

        const tl2 = cy >= 0 && cx >= 0 ? mask[cy * width + cx] : 0;
        const tr2 = cy >= 0 && cx + 1 < width ? mask[cy * width + cx + 1] : 0;
        const bl2 = cy + 1 < height && cx >= 0 ? mask[(cy + 1) * width + cx] : 0;
        const br2 = cy + 1 < height && cx + 1 < width ? mask[(cy + 1) * width + cx + 1] : 0;
        const c2 = (tl2 << 3) | (tr2 << 2) | (br2 << 1) | bl2;

        // Interpolated point on the boundary
        const px = cx + 0.5;
        const py = cy + 0.5;
        points.push([Math.round(px * 10) / 10, Math.round(py * 10) / 10]);

        // Direction based on cell config
        let dir: number;
        switch (c2) {
          case 1: case 5: case 13: dir = 0; break; // up
          case 2: case 3: case 7: dir = 1; break; // right
          case 4: case 12: case 14: dir = 3; break; // left
          case 8: case 10: case 11: dir = 2; break; // down
          case 6: dir = prevDir === 0 ? 3 : 1; break; // ambiguous
          case 9: dir = prevDir === 1 ? 0 : 2; break; // ambiguous
          default: dir = prevDir >= 0 ? prevDir : 1;
        }

        prevDir = dir;
        switch (dir) {
          case 0: cy--; break;
          case 1: cx++; break;
          case 2: cy++; break;
          case 3: cx--; break;
        }
        steps++;
      } while ((cx !== x || cy !== y) && steps < 50000 && cx >= -1 && cy >= -1 && cx < width && cy < height);

      if (points.length >= 4) {
        contours.push(rdpSimplify(points, simplify));
      }
    }
  }

  return contours;
}

function buildColorRegionSvg(
  regions: { color: string; paths: string[] }[],
  width: number, height: number,
  opts: Required<TracingOptions>,
): string {
  const bgRect = opts.backgroundColor
    ? `<rect width="${width}" height="${height}" fill="${opts.backgroundColor}"/>`
    : "";
  const regionEls = regions.map(
    (r) => r.paths.map(
      (d) => `<path d="${d}" fill="${r.color}" stroke="${r.color}" stroke-width="0.5" fill-opacity="0.85"/>`
    ).join("\n  ")
  ).join("\n  ");

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
  ${bgRect}
  ${regionEls}
</svg>`;
}

// ── Public API ──────────────────────────────────────────────────────

export async function traceImageToSvg(
  imageUrl: string,
  options: Partial<TracingOptions> = {},
): Promise<TracingResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options } as Required<TracingOptions>;
  const maxDim = opts.outputWidth || 1024;
  const { data: imageData, width, height } = await loadImageData(imageUrl, maxDim);

  let svg: string;

  if (opts.mode === "contour") {
    const edges = sobelEdgeDetection(imageData, opts.edgeThreshold);
    const paths = traceContourPaths(edges, width, height, opts.simplifyTolerance);
    svg = buildContourSvg(paths, width, height, opts);
  } else {
    const { palette, indexed } = quantizeColors(imageData, opts.colorCount);
    const regions = traceColorRegions(indexed, palette, width, height, opts.minRegionSize, opts.simplifyTolerance);
    svg = buildColorRegionSvg(regions, width, height, opts);
  }

  // Create data URL for preview
  const blob = new Blob([svg], { type: "image/svg+xml" });
  const dataUrl = URL.createObjectURL(blob);

  return { svg, width, height, mode: opts.mode, colorCount: opts.colorCount, dataUrl };
}

/** Determine which tracing mode is best based on the world setting. */
export function suggestTracingMode(setting: string): { mode: TracingMode; reason: string } {
  const s = setting.toLowerCase();
  if (s.includes("space") || s.includes("sci-fi") || s.includes("galactic")) {
    return { mode: "contour", reason: "Space maps work best with contour lines — showing orbital paths, constellation boundaries, and sector outlines." };
  }
  if (s.includes("dungeon") || s.includes("cavern") || s.includes("underground")) {
    return { mode: "contour", reason: "Dungeon layouts trace best as contour lines showing walls, passages, and chamber boundaries." };
  }
  if (s.includes("ocean") || s.includes("nautical") || s.includes("pirate")) {
    return { mode: "color_region", reason: "Nautical maps benefit from color regions — separating land masses, shallow waters, deep ocean, and reefs." };
  }
  if (s.includes("desert") || s.includes("arctic") || s.includes("tundra")) {
    return { mode: "color_region", reason: "Terrain-heavy maps work well with color regions to distinguish biomes, elevation bands, and terrain types." };
  }
  // Default fantasy
  return { mode: "color_region", reason: "Fantasy world maps with varied terrain trace best as color regions — forests, mountains, water, and plains become distinct interactive zones." };
}
